import dayjs from "dayjs";

// Business Central escribe este prefijo cuando la factura le llega sin
// identificador. Es un valor reservado: ningún guardia lo usa para reconciliar,
// ni de este lado ni del suyo (acuerdo 11, contrato de datos).
export const PREFIJO_SIN_ID = "SIN-";

/**
 * El identificador con el que Business Central reconoce una factura como
 * «la misma venta» al reintentar.
 *
 * Formato comprometido en el acuerdo 11: `{Caja}-{yyyyMMdd}-{HHmmss}-{idLocal}`.
 *
 * Los cuatro segmentos están, cada uno por su motivo:
 *
 * - **La caja** porque el `idLocal` es el contador de IndexedDB de *este*
 *   terminal: dos cajas distintas generan el mismo número sin saberlo. El
 *   formato anterior no la llevaba, y por eso el id no era único entre
 *   terminales.
 * - **Los segundos**, que antes tampoco estaban: con precisión de minuto, dos
 *   ventas del mismo minuto en la misma caja se distinguían sólo por el
 *   contador.
 * - **El contador local** es el generador de claves de IndexedDB sobre
 *   `invoices`: monótono, nunca se decrementa y no lo reinicia ningún camino
 *   del código, así que dentro de un terminal no se repite.
 *
 * Sin caja no se compone nada. Una factura sin caja no se puede mandar --el
 * envío la corta-- y un identificador con el primer segmento vacío sería
 * justamente el que puede chocar con el de otro terminal.
 *
 * @param {string} caja     Código de la caja abierta.
 * @param {number} idLocal  Clave primaria de la factura en la base local.
 * @param {Date} [ahora]    Momento del alta; parámetro para poder probarlo.
 */
export const componerIdTpv = (caja, idLocal, ahora = new Date()) => {
  const codigoDeCaja = String(caja ?? "").trim();
  if (!codigoDeCaja || !idLocal) return "";

  return `${codigoDeCaja}-${dayjs(ahora).format("YYYYMMDD-HHmmss")}-${idLocal}`;
};

/**
 * La llave con la que Business Central reconoce un alta de cliente como «el
 * mismo cliente» al reintentar.
 *
 * Es la hermana de `componerIdTpv`, pero no puede calcarlo por dos motivos:
 * un cliente **sí** puede darse de alta sin caja abierta --la pantalla de
 * Clientes es un flujo legítimo--, y en el momento de componerla la fila
 * todavía no tiene el contador de IndexedDB, porque la llave viaja dentro del
 * propio `add`.
 *
 * Sin ella, el reintento duplicaba: el mismo payload enviado dos veces --la
 * respuesta que se pierde y el lote que reenvía-- creaba dos clientes en BC
 * (caso 83 del catálogo). Y el reenvío es automático e infinito: el evento
 * `online`, el temporizador de sincronización y cada envío de factura del
 * cliente pendiente lo vuelven a mandar hasta que alguna respuesta llegue.
 *
 * El respaldo de `crypto.randomUUID` no es opcional: el entorno de pruebas
 * (jsdom 16, el de CRA 5) no trae `crypto`, y un navegador viejo de caja
 * tampoco lo garantiza.
 *
 * El campo de BC es `Code[50]` y una caja puede medir hasta 20 caracteres, así
 * que el peor caso --caja larga, guion y UUID-- se pasa: se recorta a 50. Lo
 * que se pierde es cola del aleatorio y sigue sobrando unicidad. Acordado en
 * `contrato-idempotencia-y-guardas`, ronda 4.
 *
 * @param {string} [caja] Código de la caja abierta, si la hay.
 */
export const nuevaLlaveDeAlta = (caja) => {
  const aleatorio =
    typeof crypto !== "undefined" && typeof crypto.randomUUID === "function"
      ? crypto.randomUUID()
      : `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}${Math.random()
          .toString(36)
          .slice(2, 10)}`;
  const codigoDeCaja = String(caja ?? "").trim();

  return (codigoDeCaja ? `${codigoDeCaja}-${aleatorio}` : aleatorio).slice(0, 50);
};

/**
 * Si el identificador sirve para reconciliar una factura con la que ya está en
 * Business Central.
 *
 * No sirve el vacío, ni el que empieza por `SIN-`: ese lo inventó BC porque la
 * factura le llegó sin identificador, así que no identifica nada y buscar por él
 * podría emparejar dos ventas que sólo comparten el no haberlo traído.
 */
export const esIdTpvReconciliable = (idTpv) => {
  const valor = String(idTpv ?? "").trim();
  return valor !== "" && !valor.startsWith(PREFIJO_SIN_ID);
};
